import { ChartSpec } from 'types/aiChat';
import type { FigureCell, FigureColumnType, FigureRows } from 'types/aiChatFigure';
import { CHART_COLORS, CHART_COLOR_TOKENS, ChartColorToken } from 'utils/chartColors';

/** One row per x value, or per slice for a pie: its `category` plus one field per column key. */
export type ChartRow = { category: string } & Record<string, string | number>;

export interface ChartColumn {
    key: string;
    label: string;
    color: string;
    stackId?: string;
}

function paletteColor(index: number, token?: ChartColorToken) {
    return token ? CHART_COLORS[CHART_COLOR_TOKENS.indexOf(token)] : CHART_COLORS[index % CHART_COLORS.length];
}

/**
 * The spec's series as the columns the bar and line renderers draw and the data table lists. Keys are
 * positional, never the labels: those come from the AI and may repeat or collide with `category`.
 * Series sharing a `stackId` merge into one column, so a chart-level flag hands every series the
 * same one. Bar only — a line chart ignores it.
 */
export function chartSeries(spec: ChartSpec): ChartColumn[] {
    return (spec.series ?? []).map((series, index) => ({
        key: `s${index}`,
        label: series.label,
        color: paletteColor(index, series.color),
        ...(spec.type === 'bar' && spec.stacked ? { stackId: 'stacked' } : {}),
    }));
}

/**
 * The spec pivoted into rows, the shape the chart library reads: each x value with every series'
 * value at that index under the series' column key. A pie row carries its own `fill`, and a `slice`
 * key for its legend and tooltip entry.
 */
export function chartRows(spec: ChartSpec): ChartRow[] {
    if (spec.type === 'pie') {
        return (spec.data ?? []).map((datum, index) => ({
            category: datum.label,
            slice: `s${index}`,
            value: datum.value,
            fill: paletteColor(index),
        }));
    }
    const series = spec.series ?? [];
    return (spec.x ?? []).map((category, row) => ({
        category,
        ...Object.fromEntries(series.map((entry, index) => [`s${index}`, entry.data[row]])),
    }));
}

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

/** Each series' total in column order, or a pie's: the Table view's total row. */
export function chartTotals(spec: ChartSpec): number[] {
    if (spec.type === 'pie') {
        return [sum((spec.data ?? []).map((datum) => datum.value))];
    }
    return (spec.series ?? []).map((series) => sum(series.data));
}

/**
 * A chart's key numbers: the total of its latest series (a pie's whole), and that total's change against the earlier
 * series' as a fraction, only when there are two to compare. Stacked series are parts of one whole, not two periods.
 */
export function chartKeyNumbers(spec: ChartSpec): { total: number; change: number | null } {
    const totals = chartTotals(spec);
    const total = totals.at(-1) ?? 0;
    const earlier = totals.length === 2 && !spec.stacked ? totals[0] : 0;
    return { total, change: earlier ? (total - earlier) / Math.abs(earlier) : null };
}

/** Where the latest series peaks (a pie's largest slice), for the summary a screen reader hears. */
export function chartPeak(spec: ChartSpec): { category: string; value: number } | null {
    const points =
        spec.type === 'pie'
            ? (spec.data ?? []).map(({ label, value }) => ({ category: label, value }))
            : (spec.x ?? []).map((category, index) => ({ category, value: spec.series?.at(-1)?.data[index] ?? 0 }));
    return points.reduce<{ category: string; value: number } | null>(
        (peak, point) => (!peak || point.value > peak.value ? point : peak),
        null
    );
}

/** A chart value as a Figure cell: a money value carries its currency. */
export const chartValueCell = (value: number, type: FigureColumnType, currency: string | null): FigureCell =>
    type === 'money' ? { amount: value, currency } : value;

/**
 * The chart's values as a Figure's rows: one row per category (a pie's slice), a column per series (a pie's one
 * value column), each value typed as the chart's values are. Its Table view, its Download and its copied CSV read it.
 */
export function chartFigureRows(
    spec: ChartSpec,
    labels: { category: string; value: string },
    type: FigureColumnType,
    currency: string | null
): FigureRows {
    const values =
        spec.type === 'pie'
            ? [{ key: 'value', label: labels.value }]
            : chartSeries(spec).map(({ key, label }) => ({ key, label }));
    const rows = chartRows(spec).map((row) => [
        row.category,
        ...values.map(({ key }) => chartValueCell(Number(row[key] ?? 0), type, currency)),
    ]);
    return {
        columns: [
            { field: 'category', label: labels.category, type: 'text' },
            ...values.map(({ key, label }) => ({ field: key, label, type })),
        ],
        rows,
        count: rows.length,
        partial: false,
        total: null,
        asOf: '',
    };
}
